import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import styles from './index.css';
import errorBoundary from '@ifeng-fe/errorBoundary';
import Chip from 'Chip';
import ChipEdit from 'ChipEdit';
import chipDataTransform from 'chipDataTransform';
import Footer from './footer';

const MESSAGE_TYPES = [
    { value: '政策咨询', color: 'green' },
    { value: '意见建议', color: 'red' },
    { value: '问题投诉', color: 'yellow' },
    { value: '求助纾难', color: 'blue' },
];

const TYPE_META = MESSAGE_TYPES.reduce((acc, item) => {
    acc[item.value] = item;
    return acc;
}, {});

const PAGE_SIZE = 4;
const MAX_LEN = 500;
const SUBMIT_API = 'https://d.shankapi.ifeng.com/api/liaoningBizVoice/submit';
const PHONE_REG = /^1[3-9]\d{9}$/;

const UserIcon = () => (
    <svg className={styles.avatarIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="9" r="3.5" fill="currentColor" />
        <path
            d="M5 19.5c1.8-3.2 4.2-4.8 7-4.8s5.2 1.6 7 4.8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

const ChatIcon = () => (
    <img
        className={styles.chatIcon}
        src="https://x0.ifengimg.com/ucms/2026_29/2FABD1F8BB1A816482FAA4FF5E070D9CCD3725B9_size4_w24_h23.png"
        alt=""
        aria-hidden="true"
    />
);

const SendIcon = () => (
    <img
        className={styles.sendIcon}
        src="https://x0.ifengimg.com/ucms/2026_29/D7959846FC7468F15F0319258968C3D0D77DFBD1_size4_w19_h19.png"
        alt=""
        aria-hidden="true"
    />
);

const ClockIcon = () => (
    <img
        className={styles.clockIcon}
        src="https://x0.ifengimg.com/ucms/2026_29/8DCAE364642EAA8261C8904D778603C8D52B994C_size3_w14_h14.png"
        alt=""
        aria-hidden="true"
    />
);

const ReplyIcon = () => (
    <img
        className={styles.replyIcon}
        src="https://x0.ifengimg.com/ucms/2026_29/4EBA37FA38D0CB264EF036CE2AB0BEEF608F0E60_size3_w15_h15.png"
        alt=""
        aria-hidden="true"
    />
);

const Layout = ({ pageData }) => {
    const [msgType, setMsgType] = useState('意见建议');
    const [content, setContent] = useState('');
    const [page, setPage] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [phoneModalVisible, setPhoneModalVisible] = useState(false);
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [tip, setTip] = useState('');

    const messageList = pageData && Array.isArray(pageData.messageList) ? pageData.messageList : [];
    const totalPages = Math.ceil(messageList.length / PAGE_SIZE);
    const pageMessages = messageList.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    const submitMessage = async phoneValue => {
        if (submitting) return;

        const text = content.trim();
        const trimmedPhone = phoneValue ? phoneValue.trim() : '';
        const payload = {
            type: msgType,
            content: text,
        };

        if (trimmedPhone) {
            payload.phone = trimmedPhone;
        }

        setSubmitting(true);
        setTip('');

        try {
            await axios.post(
                SUBMIT_API,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            setSubmitting(false);
            setPhoneModalVisible(false);
            setContent('');
            setPhone('');
            setPhoneError('');
            setTip('留言提交成功，我们将在3个工作日内受理并反馈。');
        } catch (err) {
            setSubmitting(false);
            setPhoneModalVisible(false);
            setTip('留言提交失败，请稍后重试。');
        }
    };

    const handleSubmit = e => {
        e.preventDefault();
        const text = content.trim();

        if (!msgType) {
            setTip('请选择留言类型');
            return;
        }
        if (!text) {
            setTip('请输入留言内容');
            return;
        }

        setTip('');
        setPhoneError('');
        setPhoneModalVisible(true);
    };

    const handlePhoneSubmit = e => {
        e.preventDefault();
        const trimmedPhone = phone.trim();

        if (trimmedPhone && !PHONE_REG.test(trimmedPhone)) {
            setPhoneError('手机号格式有误，请重新输入');
            return;
        }

        submitMessage(trimmedPhone);
    };

    const handlePhoneModalClose = () => {
        setPhone('');
        setPhoneError('');
        submitMessage();
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.inner}>
                    <a className={styles.logo} href="https://ln.ifeng.com/" target="_blank" rel="noreferrer">
                        <img
                            className={styles.logoImg}
                            src="https://x0.ifengimg.com/ucms/2026_29/9589BFF6AAA52D245B7B9DA44F795A201D4352DD_size9_w214_h45.png"
                            alt="凤凰网辽宁"
                        />
                    </a>
                </div>
            </header>

            <section className={styles.hero}>
                <div className={styles.heroInner}>
                    <div className={styles.heroText}>
                        <h1 className={styles.heroTitle}>辽宁营商环境留言板</h1>
                        <p className={styles.heroDesc}>倾听企业心声，解决实际困难。</p>
                        <p className={styles.heroDesc}>
                            欢迎您对辽宁省营商环境建设提出宝贵意见和建议，我们将认真办理并及时反馈。
                        </p>
                    </div>

                    <form className={styles.formCard} onSubmit={handleSubmit}>
                        <div className={styles.formTitle}>
                            <ChatIcon />
                            <span>我要留言</span>
                        </div>

                        <label className={styles.fieldLabel} htmlFor="msgType">
                            留言类型
                        </label>
                        <div className={styles.selectWrap}>
                            <select
                                id="msgType"
                                className={styles.select}
                                value={msgType}
                                onChange={e => setMsgType(e.target.value)}>
                                {MESSAGE_TYPES.map(item => (
                                    <option key={item.value} value={item.value}>
                                        {item.value}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.textareaWrap}>
                            <textarea
                                className={styles.textarea}
                                maxLength={MAX_LEN}
                                placeholder="请输入您的留言，我们将在3个工作日内受理，并及时向您反馈办理进展。"
                                value={content}
                                onChange={e => {
                                    setContent(e.target.value);
                                    if (tip) setTip('');
                                }}
                            />
                            <span className={styles.counter}>
                                {content.length}/{MAX_LEN}
                            </span>
                        </div>

                        {tip ? <div className={styles.tip}>{tip}</div> : null}

                        <button className={styles.submitBtn} type="submit" disabled={submitting}>
                            <SendIcon />
                            <span>{submitting ? '提交中...' : '留言'}</span>
                        </button>
                    </form>
                </div>
            </section>
            {phoneModalVisible ? (
                <div className={styles.modalOverlay} role="presentation">
                    <form className={styles.phoneModal} onSubmit={handlePhoneSubmit}>
                        <button
                            className={styles.modalClose}
                            type="button"
                            aria-label="关闭手机号填写弹窗"
                            onClick={handlePhoneModalClose}
                            disabled={submitting}>
                            ×
                        </button>
                        <h2 className={styles.modalTitle}>请留下您的手机号</h2>
                        <p className={styles.modalDesc}>
                            便于工作人员后续联系您，及时沟通办理进展并做好跟踪反馈。
                        </p>
                        <input
                            className={styles.phoneInput}
                            type="tel"
                            value={phone}
                            placeholder="请输入手机号"
                            onChange={e => {
                                setPhone(e.target.value);
                                if (phoneError) setPhoneError('');
                            }}
                            disabled={submitting}
                        />
                        {phoneError ? <div className={styles.phoneError}>{phoneError}</div> : null}
                        <div className={styles.modalActions}>
                            <button
                                className={styles.modalSecondaryBtn}
                                type="button"
                                onClick={handlePhoneModalClose}
                                disabled={submitting}>
                                暂不填写
                            </button>
                            <button className={styles.modalPrimaryBtn} type="submit" disabled={submitting}>
                                {submitting ? '提交中...' : '提交'}
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}
            <section className={styles.listSection}>
                <div className={styles.inner}>
                    <h2 className={styles.sectionTitle}>留言展示与办理进展</h2>
                    <Chip id="261179" type="struct" name="message" title="留言展示与办理进展" groupName="留言展示与办理进展">
                        <div className={styles.cardGrid}>
                            {pageMessages.map((item, idx) => {
                                const colorKey = (TYPE_META[item.type] && TYPE_META[item.type].color) || 'red';
                                const cardClass = page === 0 && idx === 0
                                    ? `${styles.msgCard} ${styles.msgCardActive}`
                                    : styles.msgCard;
                                const itemKey = item.id || `${item.time || page}-${idx}`;

                                return (
                                    <article key={itemKey} className={cardClass}>
                                        <div className={`${styles.cardHead} ${styles[`head_${colorKey}`]}`}>
                                            <div className={styles.cardHeadLeft}>
                                                <span className={styles.avatar}>
                                                    {item.img ? (
                                                        <img className={styles.avatarImg} src={item.img} alt="" />
                                                    ) : (
                                                        <UserIcon />
                                                    )}
                                                </span>
                                                <div>
                                                    <span className={`${styles.typeTag} ${styles[`tag_${colorKey}`]}`}>
                                                        {item.type}
                                                    </span>
                                                    <p className={styles.unit}>转办单位：{item.transfer_department}</p>
                                                </div>
                                            </div>
                                            <span className={styles.status}>{item.status}</span>
                                        </div>
                                        <div className={styles.cardBody}>
                                            <p className={styles.msgContent}>{item.content}</p>
                                            <div className={styles.replyBox}>
                                                <div className={styles.replyTitle}>
                                                    <ReplyIcon />
                                                    <span>官方回复</span>
                                                </div>
                                                <p className={styles.replyText}>{item.reply}</p>
                                            </div>
                                            <div className={styles.cardTime}>
                                                <ClockIcon />
                                                <span>{item.time}</span>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    </Chip>
                    <div className={styles.dots}>
                        {Array.from({ length: totalPages }).map((_, idx) => (
                            <button
                                key={idx}
                                type="button"
                                aria-label={`第${idx + 1}页`}
                                className={idx === page ? styles.dotActive : styles.dot}
                                onClick={() => setPage(idx)}
                            />
                        ))}
                    </div>
                </div>
            </section>
            <footer className={styles.footer}>
                <div className={styles.inner}>
                    <p className={styles.contact}>
                        <span>凤凰网城市联盟监督电话：010-60675241</span>
                        <span>监督及意见反馈邮箱：dujh@ifeng.com</span>
                    </p>
                </div>
            </footer>
            <Footer content={pageData.footData} />
         
            <ChipEdit transform={chipDataTransform} />
        </div>
    );
};

Layout.propTypes = {
    pageData: PropTypes.object,
};

export default errorBoundary(Layout);
